import { AlertColor } from '@mui/material';
import { isEqual, isObject, transform } from 'lodash';
import {
  Contact,
  FundedProject,
  Milestone,
  ProductFamily,
} from '../../../../types/api';
import {
  ACCOUNT_TYPES,
  DSCR_AVG_LIMIT_1,
  MS_PER_DAY,
  DSCR_AVG_LIMIT_2,
  DSCR_MIN_LIMIT_1,
  DSCR_MIN_LIMIT_2,
  isMilestoneProgram,
} from '../constants';
import { currencyFormatter } from './formatters';

export const difference = (origObj, newObj) => {
  function changes(newObj, origObj) {
    let arrayIndexCounter = 0;
    return transform(newObj, function (result: any, value, key) {
      if (!isEqual(value, origObj[key])) {
        const resultKey = Array.isArray(origObj) ? arrayIndexCounter++ : key;
        result[resultKey] =
          isObject(value) && isObject(origObj[key])
            ? changes(value, origObj[key])
            : value;
      }
    });
  }
  return changes(newObj, origObj);
};

/**
 * Recieves a sequence of callables, returns a function calling each with the given arguments
 */
export const callAll =
  (...fns) =>
  (...args) =>
    fns.forEach((fn) => fn?.(...args));

/**
 * Recurseively traverse object and return an array of paths where callback returns true
 */
export const traverse = (
  obj: any,
  callback: (value: any, key: string) => boolean,
  path: string[] = []
) => {
  return Object.entries(obj).reduce<string[]>((acc, [key, value]) => {
    if (callback(value, key)) {
      acc.push([...path, key].join('.'));
    }
    if (typeof value === 'object' && value !== null) {
      return [...acc, ...traverse(value, callback, [...path, key])];
    }
    return acc;
  }, []);
};

/**
 * Get the value of a nested object property by path, where path is a string of keys separated by dots
 */
export const getAtPath = (obj, path, defaultValue = null) => {
  const pathArray = path.split('.');
  return pathArray.reduce((acc, key) => {
    if (acc && acc[key]) {
      return acc[key];
    }
    return defaultValue;
  }, obj);
};

/**
 * Shared by LoanInformation.tsx and PriceMyProjectPage.tsx
 * filters productList by loanType
 * extracts unique combinations of loan term years and amortization years
 * returns the results sorted by loan term years and amortization years ascending order
 */

interface LoanYearsItem {
  label: string;
  value: string;
  term: number;
  amort: number;
}

// Sets the label for the 4 years term of Flex product to 1 Year Deferred
export const loanTermOptionLabel = (
  loanType: ProductFamily | undefined,
  term: number,
  defaultLabel: string
): string =>
  loanType === ProductFamily.FLEX && term === 4
    ? '1 Year Deferred'
    : defaultLabel;

export const getLoanYearsOptions = (
  productList,
  loanType,
  isLoanInformation = false
) => {
  let filtered;

  // special filtering for LoanInformation page
  if (isLoanInformation) {
    filtered = productList
      // If the selected loan type is Standard, allow both standard and premium products
      .filter(({ productFamily }) =>
        loanType === ProductFamily.STANDARD
          ? productFamily === ProductFamily.STANDARD ||
            productFamily === ProductFamily.PREMIUM
          : productFamily === loanType
      );
  } else {
    filtered = productList.filter(
      ({ productFamily }) => productFamily === loanType
    );
  }

  const newList: LoanYearsItem[] = [];
  for (const product of filtered) {
    // combine loanTermYears and amortizationYears into sortable string, e.g. '1020' or '0500'
    const defaultLabel =
      `${product.loanTermYears} years` +
      (product.amortizationYears &&
      product.loanTermYears !== product.amortizationYears
        ? ` / ${product.amortizationYears} years`
        : '');
    const value =
      product.loanTermYears.toString().padStart(2, '0') +
      (product.amortizationYears
        ? product.amortizationYears.toString().padStart(2, '0')
        : '00');
    // note: if there's no amortizationYears value, amort will be null for isLoanInformation = true and 0 for isLoanInformation = false
    newList.push({
      label: loanTermOptionLabel(loanType, product.loanTermYears, defaultLabel),
      value: value,
      term: product.loanTermYears,
      amort: product.amortizationYears,
    });
  }

  // remove duplicates and sort by year/amort
  const deDuped = newList
    .filter(
      (obj, index, self) =>
        index === self.findIndex((o) => o.label === obj.label)
    )
    .sort((a, b) => parseInt(a.value) - parseInt(b.value));

  return deDuped;
};

export const isSpecialState = (state: string) =>
  ['Colorado', 'Illinois'].includes(state);

export const isStateWithREC = (state: string) => {
  return [
    'Illinois',
    'Colorado',
    'New Jersey',
    'Washington, D.C.',
    'Maryland',
    'Ohio',
    'Pennsylvania',
  ].includes(state);
};

const preliminaryAssessmentMessageTopParagraph = `Based on the information provided, your project may be eligible for Sunstone financing. To improve the likelihood of approval, consider selecting a loan term of 10 years or less and ensure that the cost of the solar-only portion (excluding roof replacement, EV charging, and energy storage) falls within the price-per-watt thresholds outlined below.`;
const preliminaryAssessmentErrorMessageTopParagraph = `Based on the information provided, your project may be eligible for Sunstone financing. This project will likely require strong borrower credit. Please ensure that the cost of the solar-only portion (excluding roof replacement, EV charging, and energy storage) falls within the price-per-watt thresholds outlined below.`;

const preliminaryAssessmentMessageBottomParagraph =
  'You can select a loan term of 10 years or less (including the 10/20 loan) above.';

const goodDscrMessage = `Based on the information you shared, there are good chances that Sunstone will be able to provide the desired loan to your applicant.`;

// These const below are created like this since the Figma has other fields but initially we are not going to use them, but the logic is already built to scale it
const preliminaryText = {
  title: 'Preliminary assessment',
};

const successText = {
  title: 'Good news',
};

/**
 * Shared by SolarProjectInformation.tsx and SolarEconCalculator.tsx
 * Returns a DSCR result message based on loan term, DSCR score, and system location
 **/

export type Message = {
  title: string;
  main: string;
  bottomParagraph?: string;
  showPricePerWatt?: boolean;
};

export const getDSCRMessage = (solarDSCR, loanTerm): [AlertColor, Message] => {
  const avgDSCR = solarDSCR.reduce((l, r) => l + r) / solarDSCR.length;
  const minDSCR = Math.min(...solarDSCR);

  let alertType: AlertColor;
  if (loanTerm > 10) {
    if (avgDSCR >= DSCR_AVG_LIMIT_1 && minDSCR >= DSCR_MIN_LIMIT_1) {
      return ['success', { ...successText, main: goodDscrMessage }];
    } else if (avgDSCR >= DSCR_AVG_LIMIT_2 && minDSCR >= DSCR_MIN_LIMIT_2) {
      return [
        'warning',
        {
          ...preliminaryText,
          main: preliminaryAssessmentMessageTopParagraph,
          bottomParagraph: preliminaryAssessmentMessageBottomParagraph,
          showPricePerWatt: true,
        },
      ];
    } else {
      return [
        'error',
        {
          ...preliminaryText,
          main: preliminaryAssessmentMessageTopParagraph,
          bottomParagraph: preliminaryAssessmentMessageBottomParagraph,
          showPricePerWatt: true,
        },
      ];
    }
  } else {
    if (
      // Always return success for 5 year term
      loanTerm === 5 ||
      (avgDSCR >= DSCR_AVG_LIMIT_2 && minDSCR >= DSCR_MIN_LIMIT_2)
    ) {
      return ['success', { ...successText, main: goodDscrMessage }];
    } else {
      return [
        'error',
        {
          ...preliminaryText,
          main: preliminaryAssessmentErrorMessageTopParagraph,
          showPricePerWatt: true,
        },
      ];
    }
  }
};

export const getMilestoneProgramLimit = (
  project: FundedProject,
  milestone: Milestone
) => {
  const hasMilestoneProgram = isMilestoneProgram(
    project.installer_program?.type ?? ''
  );

  let maximumRequest = milestone.max_allowed_amount ?? 0;
  if (maximumRequest != null) {
    maximumRequest = parseFloat(maximumRequest.toString());
  }
  let availableBelowMaxAllowed = false;
  let limitReached = false;
  let available = project.installer_program?.amount_of_available_funds ?? 0;
  if (available != null) {
    available = parseFloat(available.toString());
    // should never happen, but this will prevent us from confusing our users
    if (available < 0) {
      available = 0;
    }
  } else {
    available = 0;
  }

  const programLimit = project.installer_program?.funding_limit ?? 0;

  const nearingLimit = available / programLimit <= 0.25;

  if (
    hasMilestoneProgram &&
    available != undefined &&
    available < maximumRequest
  ) {
    maximumRequest = available;
    availableBelowMaxAllowed = true;
    if (available === 0) {
      limitReached = true;
    }
  }

  const maximumRequestFormatted = currencyFormatter.format(maximumRequest);
  const totalAvailable = currencyFormatter.format(available);

  return {
    hasMilestoneProgram,
    maximumRequest,
    maximumRequestFormatted,
    nearingLimit,
    availableBelowMaxAllowed,
    limitReached,
    totalAvailable,
  };
};

export const verifyType = (
  value: Contact | string | null | undefined,
  targetAccountType: (typeof ACCOUNT_TYPES)[keyof typeof ACCOUNT_TYPES]
) => {
  if (value === null || value === undefined) return false;
  const accountType = typeof value === 'string' ? value : value.account.type;
  return accountType === targetAccountType;
};

export const isInstaller = (value: Contact | string | null | undefined) => {
  return verifyType(value, ACCOUNT_TYPES.INSTALLER);
};

export const isConsultant = (value: Contact | string | null | undefined) => {
  return verifyType(value, ACCOUNT_TYPES.CONSULTANT);
};

export const smallDateDiff = (date1: Date, date2: Date = new Date()) => {
  let result = '';
  const diffTime = (date2.getTime() - date1.getTime()) / 60000;
  if (diffTime < 0) {
    return '--';
  } else if (diffTime < 60) {
    result = `${Math.floor(diffTime)}m`;
  } else if (diffTime < 1440) {
    result = `${Math.floor(diffTime / 60)}h`;
  } else if (diffTime <= 172800) {
    result = `${Math.floor(diffTime / 1440)}d`;
  } else {
    result = `>120d`;
  }

  return result;
};

/**
 * e.g. "0 days ago", "1 day ago", "2 days ago".
 * Uses full 24h periods (not calendar days). Future date1 vs date2 → '--' (same idea as smallDateDiff).
 */
export const daysAgoText = (date1: Date, date2: Date = new Date()): string => {
  const diffMs = date2.getTime() - date1.getTime();
  if (diffMs < 0) {
    return '--';
  }
  const wholeDays = Math.floor(diffMs / MS_PER_DAY);
  const unit = wholeDays === 1 ? 'day' : 'days';
  return `${wholeDays} ${unit} ago`;
};

export const formatSectionAndFieldName = (name: string) => {
  return name.toLowerCase().replace(/_/g, '-');
};

export const filterApplicationsBySearch = (
  applications: any[],
  query: string
) => {
  if (!query.trim()) return applications;

  const searchTerm = query.toLowerCase();
  return applications.filter(
    (app) =>
      app.name?.toLowerCase().includes(searchTerm) ||
      app.borrower_contact_full_name?.toLowerCase().includes(searchTerm) ||
      app.borrower_business_name?.toLowerCase().includes(searchTerm)
  );
};

export const isDevEnvironment = () => {
  return process.env.NODE_ENV === 'development';
};

export const isStagingEnvironment = () => {
  return process.env.INPUT_ENVIRONMENT === 'staging';
};

export const isDevOrStagingEnvironment = () => {
  return isDevEnvironment() || isStagingEnvironment();
};

// example output: Apr 4 1452
function getFormattedDateTime() {
  const now = new Date();

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = months[now.getMonth()];
  const day = now.getDate();

  const hours = now.getHours().toString().padStart(2, '0');
  const minutes = now.getMinutes().toString().padStart(2, '0');

  return `${month} ${day} ${hours}${minutes}`;
}

function getBorrowerEmail(user) {
  if (!user) return '';
  const name = user.first_name + ' ' + user.last_name;
  if (name === 'Alex Anderson') {
    return 'alex.anderson+bor@c.sunstonecredit.com';
  } else if (name === 'Andres Guido De Innocentiis') {
    return `andres.guidosdeinnocentiis+bor@c.sunstonecredit.com`;
  } else if (name === 'Ryan Sharma') {
    return `ryan.sharma@sunstonecredit.com`;
  } else {
    return user.email.replace(/\+.*?(?=@)/, '+borrower');
  }
}

export const populateFakeData = (setValue, user) => {
  setValue('loanType', ProductFamily.STANDARD);
  setValue('loanTerm', '0505');
  setValue('application.loan_product', 'a03O300000SL8NVIA1');
  setValue('application.loan_amount', 100000);
  setValue('application.borrower_advance', 'No');

  setValue(
    'borrower.name',
    (isDevEnvironment() ? 'L ' : 'S ') +
      user?.first_name +
      ' ' +
      getFormattedDateTime()
  );
  setValue('contact.first_name', 'Test');
  setValue('contact.last_name', 'User');
  setValue('contact.position_title', 'Vizier');
  setValue('contact.email', getBorrowerEmail(user));
  setValue('contact.phone', '4155551212');

  setValue('application.sys_prop_address_state', 'California');
  setValue(
    'application.project_timeline_estimation',
    'The project is already complete'
  );
  setValue('application.system_size_in_k_ws', 140);
  setValue('application.year_1_system_production_kwh', 60000);
  setValue('application.year_1_total_solar_savings', 30000);
  setValue('application.total_system_cost', undefined);

  setValue('mounting.roof', true);
  setValue('nonSolar.hasNonSolarComponent', false);
};

export const safeJsonParse = (value?: string | null) => {
  if (!value) return undefined;

  try {
    return JSON.parse(value);
  } catch (error) {
    return undefined;
  }
};

export const parseAmount = (value: unknown): number => {
  const n =
    typeof value === 'number'
      ? value
      : Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

export const PREPAYMENT_PENALTY_SENTENCE =
  'Prepayment penalties apply for the first 5 years of the loan (Year 1 is 3%, Year 2 is 2%, Year 3 is 1%, Year 4 is 1%, and Year 5 is 1%).';

export const getRateSheetDisclaimerText = (
  isFlex: boolean,
  specialPayment: boolean,
  minSpecialPaymentPercentage: number
) => {
  const specialPaymentText = specialPayment
    ? ` Terms require at least a ${minSpecialPaymentPercentage}% special payment at month 18.`
    : '';
  if (isFlex) {
    return `Note: Price scenarios shown are purely illustrative and subject to change. 
    Pricing is locked for 90 days following credit approval subject to certain limited exceptions 
    as outlined in Sunstone Credit’s price lock policy. ${PREPAYMENT_PENALTY_SENTENCE} 
    This document does not represent a term sheet, offer or proposal of any kind. 
    Projects located in Florida incur a state stamp tax that will be charged in addition to the 
    dealer fee. The exact amount of the stamp tax will depend on the size of the loan but will not 
    be greater than $2,450. Scenario 4 loans are not eligible for any discounts or promotions.`;
  }

  return `Note: Price scenarios shown are purely illustrative and subject to change. 
        Pricing is locked for 365 days following credit approval subject
        to certain limited exceptions as outlined in Sunstone Credit's
        price lock policy.${specialPaymentText} This document does not represent
        a term sheet, offer or proposal of any kind. Projects located in Florida
        incur a state stamp tax that will be charged in addition to the dealer
        fee. The exact amount of the stamp tax will depend on the size of the
        loan but will not be greater than $2,450. Scenario ${
          minSpecialPaymentPercentage === 20 ? '3' : '4'
        } loans are not
        eligible for any discounts or promotions.`;
};
